import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { getProjectBySlug, projects } from "../data/projects";

export default function ProjectDetail() {
    const { slug } = useParams();
    const project = getProjectBySlug(slug);

    if (!project) {
        return (
            <section
                data-testid="project-not-found"
                className="max-w-3xl mx-auto px-6 py-32 text-center"
            >
                <div className="overline mb-4">404</div>
                <h1 className="font-serif text-5xl text-[#2C241B] mb-6">
                    Projektet blev ikke fundet
                </h1>
                <p className="text-[#4A3B2C] mb-10">
                    Måske er det flyttet eller endnu ikke publiceret.
                </p>
                <Link
                    to="/projekter"
                    data-testid="back-to-projects"
                    className="inline-flex items-center gap-2 bg-[#2F483A] text-[#F9F6F0] px-8 py-4 text-xs uppercase tracking-[0.22em] rounded-sm"
                >
                    <ArrowLeft size={14} /> Tilbage til projekter
                </Link>
            </section>
        );
    }

    const currentIndex = projects.findIndex((p) => p.slug === project.slug);
    const next = projects[(currentIndex + 1) % projects.length];

    return (
        <div data-testid="project-detail-page">
            {/* HERO */}
            <section className="relative">
                <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-16">
                    <Link
                        to="/projekter"
                        data-testid="back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#4A3B2C] hover:text-[#2C241B] link-reveal"
                    >
                        <ArrowLeft size={14} /> Alle projekter
                    </Link>

                    <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                        <div className="md:col-span-8 fade-up">
                            <div className="overline mb-4">
                                {project.category} · {project.year}
                            </div>
                            <h1 className="font-serif text-5xl md:text-7xl leading-[1.02] text-[#2C241B]">
                                {project.title}
                            </h1>
                            {project.subtitle && (
                                <p className="mt-5 text-lg text-[#4A3B2C] font-light">
                                    {project.subtitle}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="mt-12 md:mt-16 max-w-[1400px] mx-auto px-6 md:px-10">
                    <div className="img-hover overflow-hidden rounded-sm aspect-[16/10] md:aspect-[21/9] bg-[#EAE3D5]">
                        <img
                            src={project.cover}
                            alt={project.title}
                            className="w-full h-full object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* CONTENT */}
            <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-14">
                <div className="md:col-span-8 md:col-start-1">
                    <div className="overline mb-4">Om opgaven</div>
                    <div className="prose-ff space-y-6">
                        {project.description.map((para, idx) => (
                            <p
                                key={idx}
                                className="text-lg md:text-xl text-[#2C241B] leading-[1.7] font-light"
                            >
                                {para}
                            </p>
                        ))}
                    </div>

                    {project.summary && (
                        <blockquote className="mt-12 border-l-2 border-[#8B5E3C] pl-6 md:pl-8 py-2 font-serif italic text-2xl md:text-3xl text-[#2F483A] leading-snug">
                            {project.summary}
                        </blockquote>
                    )}
                </div>

                <aside className="md:col-span-4 md:col-start-9">
                    <div className="sticky top-28 space-y-8">
                        <div>
                            <div className="overline mb-3">Detaljer</div>
                            <dl className="text-sm text-[#4A3B2C] divide-y divide-[#D1C7B7]">
                                <div className="flex justify-between py-3">
                                    <dt className="text-[#7A6855]">Type</dt>
                                    <dd className="text-[#2C241B]">
                                        {project.category}
                                    </dd>
                                </div>
                                <div className="flex justify-between py-3">
                                    <dt className="text-[#7A6855]">År</dt>
                                    <dd className="text-[#2C241B]">
                                        {project.year}
                                    </dd>
                                </div>
                                {project.subtitle && (
                                    <div className="flex justify-between py-3">
                                        <dt className="text-[#7A6855]">
                                            Sted
                                        </dt>
                                        <dd className="text-[#2C241B] text-right">
                                            {project.subtitle}
                                        </dd>
                                    </div>
                                )}
                            </dl>
                        </div>

                        <div>
                            <div className="overline mb-3">
                                Se opslaget originalt
                            </div>
                            <div className="flex flex-col gap-3">
                                {project.facebookUrl && (
                                    <a
                                        href={project.facebookUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="project-facebook-link"
                                        className="inline-flex items-center justify-between gap-3 border border-[#2C241B]/30 hover:border-[#2F483A] hover:text-[#2F483A] px-5 py-4 rounded-sm transition-colors"
                                    >
                                        <span className="inline-flex items-center gap-3">
                                            <Facebook size={16} />
                                            <span className="text-sm uppercase tracking-[0.18em]">
                                                Facebook
                                            </span>
                                        </span>
                                        <ArrowUpRight size={16} />
                                    </a>
                                )}
                                {project.instagramUrl && (
                                    <a
                                        href={project.instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="project-instagram-link"
                                        className="inline-flex items-center justify-between gap-3 border border-[#2C241B]/30 hover:border-[#2F483A] hover:text-[#2F483A] px-5 py-4 rounded-sm transition-colors"
                                    >
                                        <span className="inline-flex items-center gap-3">
                                            <Instagram size={16} />
                                            <span className="text-sm uppercase tracking-[0.18em]">
                                                Instagram
                                            </span>
                                        </span>
                                        <ArrowUpRight size={16} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </aside>
            </section>

            {/* GALLERY */}
            {project.gallery && project.gallery.length > 0 && (
                <section className="max-w-7xl mx-auto px-6 md:px-10 pb-24">
                    <div className="overline mb-8">Fra værkstedet</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                        {project.gallery.map((src, i) => (
                            <div
                                key={i}
                                className={`img-hover overflow-hidden rounded-sm bg-[#EAE3D5] ${
                                    i % 3 === 0
                                        ? "aspect-[4/5]"
                                        : "aspect-[4/3]"
                                }`}
                                data-testid="project-gallery-image"
                            >
                                <img
                                    src={src}
                                    alt={`${project.title} – billede ${i + 1}`}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* NEXT */}
            {next && next.slug !== project.slug && (
                <section className="border-t border-[#D1C7B7]">
                    <Link
                        to={`/projekter/${next.slug}`}
                        data-testid="next-project-link"
                        className="group block max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center"
                    >
                        <div className="md:col-span-8">
                            <div className="overline mb-3">Næste projekt</div>
                            <div className="font-serif text-4xl md:text-6xl text-[#2C241B] group-hover:text-[#2F483A] transition-colors leading-tight">
                                {next.title}
                            </div>
                        </div>
                        <div className="md:col-span-4 flex md:justify-end">
                            <ArrowUpRight
                                size={36}
                                className="text-[#8B5E3C] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500"
                            />
                        </div>
                    </Link>
                </section>
            )}
        </div>
    );
}
